import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import { saveTableColumns } from '../../actions/app'
import { colors, Tables } from '../../constants'
import { getTableColumns } from '../../reducers/app'
import { Button, Centered, Column, Expander, Headings, Input, Loader, SearchInput } from '..'
import { useSize } from '../utils'
import ColumnSelector from './ColumnSelector'
import * as TableProps from './Table.propTypes'
import { Table, TableBody, TableHeader, Title, Header, SortedAsc, SortedDesc, Row, Cell, ColSelectorCell } from './Table.style'
import NextPage from './NextPage'
import { Confirmation } from '../../components'
import { Icon } from '../../components/Screens/Estate/Store/Store.styles'
import { IDUtils } from '../../utils'
import * as NotificationsActions from '../../actions/notifications'

function TableComponent(props) {
    const {
        addButton,
        addWithSuggestions,
        autoscroll,
        children,
        childrenExpanderHeight,
        complexTable,
        currentPage,
        data: propData,
        display: propDisplay,
        expander,
        fieldsNotToSort,
        hideDeviceButtons,
        isLoading,
        keyField,
        maxCols,
        nextPage,
        noMarginTop,
        onChangeSearch,
        onChangeSort,
        onClickRow,
        searchedItem,
        selectedItem,
        sortable,
        tableKey,
        sortableByAPI,
        title,
        underlineExpiryDateItem,
        underlineSearchedItem,
        valueSearch,
        withColSelection,
        rebootDevice,
        resetSelection,
        restartApp,
        clearCacheDevice,
        clearContentDevice,
        ...rest
    } = props

    /**
     * Items: { field: [string], asc: [boolean] }
     */
    const [sortBy, setSortBy] = React.useState([])
    const [stateColumns, setStateColumns] = React.useState([])
    const [isLoadingNextPage, setIsLoadingNextPage] = React.useState(false)
    const scrollRef = React.useRef(null)
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const savedColumns = useSelector((state) => getTableColumns(state, tableKey))
    const {
        isMobile,
        size: {
            width: { isLG, isXL },
        },
    } = useSize()

    /**
     * 
     * @param {Function} fn 
     * @param {'reboot' | 'reload'} command 
     */
    const allDevicesCommand = (fn, command) => {
        getData()
            .filter((uid) => uid.selected)
            .forEach((uid, index) =>
                setTimeout(async () => {
                    fn(uid)
                    sendNotification(command, index + 1, selectedDevicesCount())
                }, index * 1000)
            )
        resetSelection()
    }

    const rebootAllDevices = () => allDevicesCommand(
        (uid) => rebootDevice(uid.id, IDUtils.generateSocketGroupIDReboot()),
        'reboot',
    )

    const restartAppInAllDevices = () => allDevicesCommand(
        (uid) => restartApp(uid.id, IDUtils.generateSocketGroupIDReboot()),
        'reload',
    )

    const clearCacheInAllDevices = () => allDevicesCommand(
        (uid) => clearCacheDevice(uid.id, IDUtils.generateSocketGroupIDReboot()),
        'clear-cache',
    )

    const clearContentInAllDevices = () => allDevicesCommand(
        (uid) => clearContentDevice(uid.id, IDUtils.generateSocketGroupIDReboot()),
        'clear-content',
    )

    const sendNotification = async (kind, index, length) => {
        let title = ''
        let body = ''
        switch (kind) {
            case 'reboot':
                title = t('message-reboot', { context: 'title' })
                body = t('message-reboot-multiple', {
                    context: 'body',
                    index,
                    length,
                })
                break
            case 'reload':
                title = t('message-reload', { context: 'title' })
                body = t('message-reload-multiple', {
                    context: 'body',
                    index,
                    length,
                })
                break
            default:
                break
        }
        await dispatch(NotificationsActions.notify({ title, body }))
    }

    const getSelectedColumns = () => {
        if (tableKey) {
            return savedColumns || []
        }

        return stateColumns
    }

    const saveSelectedColumns = (value) => {
        if (tableKey) {
            return dispatch(saveTableColumns(tableKey, value))
        }

        setStateColumns(value)
    }

    const getDisplay = () => {
        return (
            propDisplay &&
            propDisplay.filter(
                ({ onlyOnLG, onlyOnMobile, onlyOnXL }) =>
                    (onlyOnMobile && isMobile) || // Only on mobile
                    (!isMobile &&
                        !onlyOnMobile &&
                        ((!onlyOnLG && !onlyOnXL) || // Neither of flags are specified
                            (onlyOnLG && (isLG || isXL)) || // Only on LG and is LG or XL
                            (onlyOnXL && isXL))) // Only on XL and is LG
            )
        )
    }

    const buildSelectedColumns = () => {
        const firstItem = propData[0] || {}
        const display = getDisplay()
        let selectedColumnsValue

        if (display) {
            selectedColumnsValue = display.filter(({ selectable = '' }) => !['mandatory', 'hidden'].includes(selectable)).map(({ key }) => key)
        } else {
            selectedColumnsValue = Object.keys(firstItem).slice(0, withColSelection ? maxCols - 1 : maxCols)
        }

        saveSelectedColumns(selectedColumnsValue)
    }

    /**
     * Initializes `sortBy` array.
     * If `sortable` is an array instead of a boolean, it sorts the table
     * by default by the fields contained on the array.
     */
    const initializeState = () => {
        let sortByValue = []

        if (sortable instanceof Array) {
            sortByValue = sortable.map((field) => ({
                field,
                asc: true,
            }))
        } else if (sortableByAPI instanceof Array) {
            sortByValue = sortableByAPI.map((field) => ({
                field: field.fieldToSort,
                asc: field.sortType === 'ASC',
            }))
        }

        setSortBy(sortByValue)

        if (!savedColumns) {
            buildSelectedColumns()
        }
    }

    const shouldAutoLoadNextPage = () => {
        const { scrollHeight, scrollTop, clientHeight } = scrollRef.current
        return scrollHeight - scrollTop <= clientHeight
    }

    const onScrollEvent = async () => {
        if (!autoscroll) {
            return
        }

        if (isLoading || isLoadingNextPage) {
            return
        }

        const nextPageIsFunc = typeof nextPage === 'function'

        if (shouldAutoLoadNextPage() && nextPageIsFunc) {
            await nextPage()
        }
    }
    /**
     * Gets the data properly sorted
     */
    const getData = () => {
        // If table is not sortable
        if (!sortable && !sortableByAPI) {
            return propData
        }

        if (sortable)
            return propData.sort((a, b) => {
                // If there's not field specified for sorting, take keyfield if available
                if (!sortBy.length && keyField) {
                    const { [keyField]: keyA } = a
                    const { [keyField]: keyB } = b

                    if (keyA === keyB) return 0
                    return keyA < keyB ? -1 : 1
                }

                // Compare by every field until they are not equal
                for (let i = 0; i < sortBy.length; i += 1) {
                    const { field, asc } = sortBy[i]

                    // If field is null, don not compare this iteration
                    if (!field) {
                        continue // eslint-disable-line no-continue
                    }

                    const elemA = a[field] instanceof Array ? a[field].length : a[field]
                    const elemB = b[field] instanceof Array ? b[field].length : b[field]

                    // asc order
                    if (asc) {
                        // If the elements are string, use `localeCompare`
                        if (typeof elemA === 'string' && typeof elemB === 'string') {
                            return elemA.localeCompare(elemB)
                        }

                        if (elemA === null) {
                            return 1
                        }
                        if (elemB === null) {
                            return -1
                        }

                        if (elemA < elemB) {
                            return -1
                        }

                        if (elemA > elemB) {
                            return 1
                        }

                        if (elemA?.name < elemB?.name) {
                            return -1
                        }

                        if (elemA?.name > elemB?.name) {
                            return 1
                        }
                    } else {
                        // desc order
                        // If the elements are string, use `localeCompare`
                        if (typeof elemA === 'string' && typeof elemB === 'string') {
                            return elemB.localeCompare(elemA)
                        }

                        if (elemA === null) {
                            return 1
                        }
                        if (elemB === null) {
                            return -1
                        }

                        if (elemA < elemB) {
                            return 1
                        }

                        if (elemA > elemB) {
                            return -1
                        }
                        if (elemA?.name > elemB?.name) {
                            return -1
                        }
                        if (elemA?.name < elemB?.name) {
                            return 1
                        }
                    }
                }

                // If reached here, they are equal
                return 0
            })

        return propData
    }

    /**
     * Changes a field sorting status
     * Logic: - first click on label, asc order (asc true).
     *        - second click on label, desc order (asc false).
     *        - third click on label, stop ordering by this field.
     * @param {string} field Field to sort by
     */
    const onClickHeaderLabel = (field) => {
        if ((!sortable && !sortableByAPI) || !field) {
            return null
        }

        const lastItem = sortBy.find(({ field: itemField }) => field === itemField)

        let newSortBy

        // If already sorting by this field
        if (lastItem) {
            const index = sortBy.indexOf(lastItem)
            const { asc } = lastItem

            // If sorting asc -> change it to desc
            if (asc) {
                newSortBy = [
                    ...sortBy.slice(0, index),
                    {
                        field,
                        asc: false,
                    },
                    ...sortBy.slice(index + 1, sortBy.length),
                ]
            } else {
                // if not sorting asc (sorting desc) -> remove item
                newSortBy = [...sortBy.slice(0, index), ...sortBy.slice(index + 1, sortBy.length)]
            }
        }
        // If sorting by API, you can only sort by 1 column
        else if (sortableByAPI) {
            newSortBy = [
                {
                    field,
                    asc: true,
                },
            ]
            // if not sorting by the field, add it with asc order
        } else {
            newSortBy = [
                ...sortBy,
                {
                    field,
                    asc: true,
                },
            ]
        }

        setSortBy(newSortBy)

        if (onChangeSort) {
            onChangeSort(
                newSortBy.map(({ field: fieldToSort, asc }) => ({
                    fieldToSort,
                    sortType: asc ? 'ASC' : 'DESC',
                }))
            )
        }
    }

    const onChangeColumnsSelector = ({ target: { value } }) => {
        saveSelectedColumns(value)
    }

    /**
     * Renders the chevron icon for a certain field if it's ordered by
     * @param {string} field Check if sorting by this field
     */
    const renderSortingLabel = (field) => {
        if ((!sortable && !sortableByAPI) || !field) {
            return null
        }

        const sortItem = sortBy.find(({ field: itemField }) => itemField === field)

        if (!sortItem) {
            return null
        }

        return sortItem.asc ? <SortedAsc /> : <SortedDesc />
    }

    /**
     * Renders the columns names header
     */
    const renderHeader = () => {
        const selectedColumns = getSelectedColumns()
        const firstItem = propData[0] || {}
        const display = getDisplay()
        let header

        if (display) {
            header = display
                .filter(({ key, selectable }) => {
                    if (!withColSelection) {
                        return true
                    }

                    return selectable === 'mandatory' || selectedColumns.includes(key)
                })
                .slice(0, maxCols)
                .map(({ field, key, label, props: subProps = {}, width, header }, index) => {
                    // const value = header instanceof Function ? header(

                    // eslint-disable-next-line react/jsx-indent
                    return (
                        <Cell
                            onClick={() => label && !(fieldsNotToSort.length > 0 && fieldsNotToSort.includes(field)) && onClickHeaderLabel(field)} // Not to sort columns without label
                            width={width}
                            key={key || label || field || index}
                            withPointer={label && (sortable || sortableByAPI) && !(fieldsNotToSort.length > 0 && fieldsNotToSort.includes(field))}
                            {...subProps}
                        >
                            {header}
                            {label}
                            {label && renderSortingLabel(field)}
                        </Cell>
                    )
                })
        } else {
            header = Object.keys(firstItem)
                .filter((field) => (withColSelection ? selectedColumns.includes(field) : true))
                .slice(0, withColSelection ? maxCols - 1 : maxCols)
                .map((k) => (
                    <Cell
                        key={k}
                        onClick={k && !(fieldsNotToSort.length > 0 && fieldsNotToSort.includes(k)) && (() => onClickHeaderLabel(k))}
                        withPointer={(sortable || sortableByAPI) && !(fieldsNotToSort.length > 0 && fieldsNotToSort.includes(k))}
                    >
                        {k}
                        {k && renderSortingLabel(k)}
                    </Cell>
                ))
        }

        if (withColSelection) {
            const cols = display || Object.keys(firstItem)
            header.push(
                <ColSelectorCell key="col-select" align="right" width="150px">
                    <ColumnSelector columns={cols} onChange={onChangeColumnsSelector} selectedColumns={selectedColumns}>
                        {isLG ? t('table-add-hide-cols') : <>&hellip;</>}
                    </ColumnSelector>
                </ColSelectorCell>
            )
        }

        return header
    }

    const renderSuggestions = () => {
        if (addWithSuggestions) {
            const { onChange, onTextChange, options, placeholder } = addWithSuggestions
            return (
                <Row>
                    <Input
                        type="suggestions"
                        onChange={onChange}
                        onTextChange={onTextChange}
                        options={options}
                        placeholder={placeholder}
                        notRelative
                        theme="secondary"
                    />
                </Row>
            )
        }
    }

    /**
     * Renders one item row
     * @param {object} item One item from the data array
     */
    const renderRow = (item) => {
        const selectedColumns = getSelectedColumns()
        const display = getDisplay()

        if (display) {
            return display
                .filter(({ key, selectable }) => {
                    if (!withColSelection) {
                        return true
                    }

                    return selectable === 'mandatory' || selectedColumns.includes(key)
                })
                .slice(0, maxCols)
                .map(({ field, key, label, props: rowProps = {}, transform, width }, index) => {
                    const value = transform instanceof Function ? transform(field ? item[field] : item) : field ? item[field] : ''

                    return (
                        <Cell
                            expander={expander}
                            key={key || label || field || index}
                            title={typeof value === 'string' ? value : null}
                            width={width}
                            {...rowProps}
                        >
                            {value}
                        </Cell>
                    )
                })
        }

        return Object.keys(item)
            .filter((field) => (withColSelection ? selectedColumns.includes(field) : true))
            .slice(0, withColSelection ? maxCols - 1 : maxCols)
            .map((k) => (
                <Cell key={k} expander={expander}>
                    {item[k]}
                </Cell>
            ))
    }

    function selectedDevicesCount() {
        return getData().filter((obj) => obj.selected).length
    }
    /**
     * Renders the all the rows with the table data
     */
    const renderBody = () => {
        const onClick = onClickRow || (() => null)

        return (
            <>
                {getData().map((item, index) => {
                    const key = keyField && item[keyField]

                    let found = false
                    if (searchedItem && underlineSearchedItem) found = underlineSearchedItem(item, searchedItem)
                    let expired = false
                    if (underlineExpiryDateItem) expired = underlineExpiryDateItem(item)

                    // eslint-disable-next-line eqeqeq
                    const selected = key == selectedItem

                    const striped = index % 2

                    return (
                        <Row
                            clickable={!!onClickRow}
                            onClick={() => onClick(key)}
                            key={key}
                            found={found}
                            expired={expired}
                            selected={selected}
                            striped={striped}
                            noOverflow={!!expander}
                        >
                            {expander ? (
                                <Expander
                                    onlyLeftChevron
                                    header={renderRow(item)}
                                    isOpen={selected}
                                    changeVisibility={() => null}
                                    childrenHeight={childrenExpanderHeight}
                                    color={selected ? colors.white : colors.bluedarkest}
                                    inTable
                                    noAnimation
                                >
                                    {expander(selected, item)}
                                </Expander>
                            ) : (
                                renderRow(item)
                            )}
                        </Row>
                    )
                })}
                {renderSuggestions()}
                {!isLoading && !isLoadingNextPage && typeof nextPage === 'function' && <NextPage onClick={nextPage}>{`${t('Load more')}...`}</NextPage>}
                {(isLoading || isLoadingNextPage) && (
                    <Centered horizontal>
                        <Loader />
                    </Centered>
                )}
            </>
        )
    }

    React.useEffect(() => {
        initializeState()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
        if (!autoscroll) {
            return
        }

        if (currentPage !== 1) {
            return
        }

        const nextPageIsFunc = typeof nextPage === 'function'

        if (shouldAutoLoadNextPage() && nextPageIsFunc) {
            const loadMore = async () => {
                setIsLoadingNextPage(true)
                await nextPage()
                setIsLoadingNextPage(false)
            }

            loadMore()
        }
    }, [autoscroll, currentPage, nextPage])

    const withTitle = !!title

    return (
        <Table {...rest}>
            <TableHeader>
                {!withTitle && children}
                {withTitle && (
                    <Title>
                        <Column>
                            <Headings.H2 color={colors.bluedark}>{title}</Headings.H2>
                        </Column>
                        <Column right>{!!onChangeSearch && <SearchInput onChange={onChangeSearch} value={valueSearch} />}</Column>
                    </Title>
                )}
                <Header>{renderHeader()}</Header>
            </TableHeader>
            <TableBody noMarginTop={noMarginTop || !withTitle} withTitle={withTitle} complexTable={complexTable} ref={scrollRef} onScroll={onScrollEvent}>
                {renderBody()}
            </TableBody>
            {!hideDeviceButtons && selectedDevicesCount() > 0 && (
                <>
                    <Confirmation
                        confirmation={t('RestartAllConfirmBody', {
                            count: selectedDevicesCount(),
                        })}
                        header={t('RestartAll')}
                        ok={t('ok')}
                        cancel={t('cancel')}
                        onCancel={() => null}
                        onConfirm={restartAppInAllDevices}
                    >
                        <div className="bulk-tools restart-tool">
                            <Icon
                                className="bulk-action-button restart-button"
                                icon="redo"
                                title={t('RestartAll')}
                            />
                        </div>
                    </Confirmation>
                    <Confirmation
                        confirmation={t('RebootAllConfirmBody', {
                            count: selectedDevicesCount(),
                        })}
                        header={t('RebootAll')}
                        ok={t('ok')}
                        cancel={t('cancel')}
                        onCancel={() => null}
                        onConfirm={rebootAllDevices}
                    >
                        <div className="bulk-tools reboot-tool">
                            <Icon
                                className="bulk-action-button"
                                icon="bolt"
                                title={t('RebootAll')}
                            />
                        </div>
                    </Confirmation>
                    {clearContentDevice && (
                        <Confirmation
                            confirmation={t('device-clear-content-confirm-body', {
                                count: selectedDevicesCount(),
                            })}
                            header={t('device-clear-content')}
                            ok={t('ok')}
                            cancel={t('cancel')}
                            onCancel={() => null}
                            onConfirm={clearContentInAllDevices}
                        >
                            <div className="bulk-tools clear-content-tool">
                                <Icon className="bulk-action-button clear-content-button"
                                    icon="poo-storm"
                                    title={t('device-clear-content')}
                                />
                            </div>
                        </Confirmation>)
                    }
                    {clearCacheDevice && (
                        <Confirmation
                            confirmation={t('device-clear-cache-confirm-body', {
                                count: selectedDevicesCount(),
                            })}
                            header={t('device-clear-cache')}
                            ok={t('ok')}
                            cancel={t('cancel')}
                            onCancel={() => null}
                            onConfirm={clearCacheInAllDevices}
                        >
                            <div className="bulk-tools clear-cache-tool">
                                <Icon
                                    className="bulk-action-button clear-cache-button"
                                    icon="folder-minus"
                                    title={t('device-clear-cache')}
                                />
                            </div>
                        </Confirmation>
                    )}
                </>
            )}
            {addButton && (
                <Button className="add-button" icon="plus" rightBottonCorner onClick={addButton} size="sm" style={{ textTransform: 'capitalize' }}>
                    {t('add')}
                </Button>
            )}
        </Table>
    )
}

TableComponent.propTypes = {
    addButton: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    rebootAllButton: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    addWithSuggestions: PropTypes.shape({
        onChange: PropTypes.func.isRequired,
        onTextChange: PropTypes.func.isRequired,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                value: PropTypes.string.isRequired,
            })
        ),
        placeholder: PropTypes.string.isRequired,
    }),
    autoscroll: PropTypes.bool,
    children: PropTypes.element,
    childrenExpanderHeight: PropTypes.number,
    complexTable: PropTypes.bool,
    currentPage: PropTypes.number,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    display: PropTypes.arrayOf(TableProps.displayItem),
    expander: PropTypes.func,
    fieldsNotToSort: PropTypes.arrayOf(PropTypes.string),
    hideDeviceButtons: PropTypes.bool,
    isLoading: PropTypes.bool,
    keyField: PropTypes.string,
    maxCols: PropTypes.number,
    nextPage: PropTypes.func,
    noMarginTop: PropTypes.bool,
    onChangeSearch: PropTypes.func,
    onChangeSort: PropTypes.func,
    onClickRow: PropTypes.func,
    searchedItem: PropTypes.string,
    selectedItem: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sortable: PropTypes.oneOfType([PropTypes.bool, PropTypes.arrayOf(PropTypes.string)]),
    sortableByAPI: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.arrayOf(
            PropTypes.shape({
                fieldToSort: PropTypes.string,
                sortType: PropTypes.string,
            })
        ),
    ]),
    tableKey: PropTypes.oneOf(Object.values(Tables)),
    title: PropTypes.string,
    underlineExpiryDateItem: PropTypes.func,
    underlineSearchedItem: PropTypes.func,
    valueSearch: PropTypes.string,
    withColSelection: PropTypes.bool,
    resetSelection: PropTypes.func,
    clearCacheDevice: PropTypes.func,
    clearContentDevice: PropTypes.func,
    rebootDevice: PropTypes.func,
    restartApp: PropTypes.func,
    style: PropTypes.object,
}

TableComponent.defaultProps = {
    addButton: null,
    rebootAllButton: null,
    addWithSuggestions: null,
    autoscroll: false,
    children: null,
    childrenExpanderHeight: 0,
    complexTable: false,
    currentPage: 1,
    data: [],
    display: null,
    expander: null,
    fieldsNotToSort: [],
    hideDeviceButtons: false,
    isLoading: false,
    keyField: '',
    maxCols: 20,
    nextPage: null,
    noMarginTop: false,
    onChangeSearch: null,
    onChangeSort: null,
    onClickRow: null,
    searchedItem: '',
    selectedItem: null,
    sortable: false,
    sortableByAPI: false,
    tableKey: null,
    title: null,
    underlineExpiryDateItem: null,
    underlineSearchedItem: () => null,
    valueSearch: '',
    withColSelection: false,
    resetSelection: () => null,
}

export default TableComponent
